import { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';

const useWebSocket = (user, onMessage) => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    if (!user?.email) {
      console.log('⏸️  WebSocket: No user email, skipping connection');
      return;
    }

    const connect = () => {
      try {
        // WebSocket runs on the API server (Railway/local), not on the frontend host (Netlify)
        const apiUrl = API_BASE_URL || '';
        const isHttps = apiUrl.startsWith('https:');
        const protocol = isHttps ? 'wss:' : 'ws:';
        const host = apiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
        const wsUrl = host ? `${protocol}//${host}/ws` : null;

        if (!wsUrl) {
          console.log('⏸️  WebSocket: No API URL, skipping');
          return;
        }

        console.log(`🔌 Connecting to WebSocket: ${wsUrl}`);
        ws.current = new WebSocket(wsUrl);

        ws.current.onopen = () => {
          console.log('✅ WebSocket connected');
          setIsConnected(true);
          reconnectAttempts.current = 0;

          // Authenticate with user email
          ws.current.send(JSON.stringify({
            type: 'auth',
            email: user.email
          }));
        };

        ws.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 WebSocket message:', data);

            if (data.type === 'auth_success') {
              console.log('✅ WebSocket authenticated');
            } else if (data.type === 'sync_complete') {
              console.log(`🔄 Sync complete for ${data.accountName}`);
              if (onMessage) {
                onMessage(data);
              }
            }
          } catch (error) {
            console.error('❌ Error parsing WebSocket message:', error);
          }
        };

        ws.current.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
        };

        ws.current.onclose = () => {
          console.log('🔌 WebSocket disconnected');
          setIsConnected(false);

          // Attempt to reconnect with exponential backoff
          if (reconnectAttempts.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
            
            reconnectTimeout.current = setTimeout(() => {
              reconnectAttempts.current++;
              connect();
            }, delay);
          } else {
            console.log('❌ Max reconnection attempts reached');
          }
        };
      } catch (error) {
        console.error('❌ Error creating WebSocket:', error);
      }
    };

    connect();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [user?.email, onMessage]);

  return { isConnected };
};

export default useWebSocket;
